import { generatePath, useParams } from 'react-router-dom';

import { ErrorScreen } from '@components/error-screen';
import { FormBuilder } from '@components/form-builder';
import { FormV2 } from '@components/form/v2';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { RelativeSpinner } from '@components/loading-spinner';
import { useScopedTranslate } from '@locale/translate';

import {
  useCreateFormTemplateImage,
  useEditTemplate,
  useFormTemplate,
  useFormTemplateJourneys,
} from 'api/forms/hooks';
import { useFormBuilderLists } from 'api/lists/hooks';
import { routePaths } from 'ui/authenticated/navigation';
import { useFeature } from 'ui/hooks';

import { JourneysTabPage } from './journeys-tab';

export const FormTemplatesEdit = () => {
  const { shortId } = useParams();
  const { t } = useScopedTranslate(locale);
  const { isEnabled: hasKennitalaEnabled } = useFeature('FEATURE_KENNITALA');
  const { data, isError, isLoading } = useFormTemplate(shortId as string);
  const journeyQuery = useFormTemplateJourneys(shortId as string);
  const createFormTemplateImageQuery = useCreateFormTemplateImage();
  const editTemplate = useEditTemplate();
  const { data: lists } = useFormBuilderLists();

  if (isError) {
    return <ErrorScreen code={404} message={t('notFoundMessage')} heading={t('notFound')} />;
  }

  const journeys = journeyQuery.isSuccess ? journeyQuery.data : [];

  if (isLoading) {
    return <RelativeSpinner />;
  }

  return (
    <FormBuilder
      predefinedOptionsLists={lists}
      form={data.fieldsConfig}
      submissionMessage={data.submissionMessageConfig}
      name={data.name}
      description={data.description || ''}
      showKennitala={hasKennitalaEnabled}
      backUrl={generatePath(routePaths.workspaceFormTemplatesList, {
        workspaceId: data.workspace,
      })}
      onSave={async (template, onSaveSuccess) => {
        const response = await editTemplate.mutateAsync({
          ...data,
          ...template,
        });
        onSaveSuccess?.(
          response.fieldsConfig as FormV2,
          response.submissionMessageConfig as FormV2
        );
      }}
      extraTabs={[
        {
          label: t('journeys'),
          section: 'journeys',
          dataTestId: 'formbuilder-journeys',
          content: <JourneysTabPage journeys={journeys} />,
          icon: <IconJourneyItem />,
          counter: journeys.length,
        },
      ]}
      onImageSave={async (props) => {
        const response = await createFormTemplateImageQuery.mutateAsync(props);
        return response.imageId;
      }}
    />
  );
};

const locale = {
  en: {
    journeys: 'Journeys',
    notFound: 'Not found',
    notFoundMessage: 'Form template not found, are you sure it exists?',
  },
};
